/**
 * SourceInputSection Component
 *
 * Collapsible section for recipe source input with tabs for text, URL, or image.
 */

import { useRef } from 'react';
import type { ExtractSourceType } from '@/types/recipeImport';
import { SourceTypeTabs } from './SourceTypeTabs';

interface SourceInputSectionProps {
  sourceType: ExtractSourceType;
  onSourceTypeChange: (type: ExtractSourceType) => void;
  textContent: string;
  onTextContentChange: (value: string) => void;
  urlContent: string;
  onUrlContentChange: (value: string) => void;
  imageFile: File | null;
  onImageFileChange: (file: File | null) => void;
  pdfFile: File | null;
  onPdfFileChange: (file: File | null) => void;
  onExtract: () => void;
  isExtracting: boolean;
}

/**
 * Upload icon for image upload button
 */
function UploadIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}

/**
 * X icon for clear button
 */
function XIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

/**
 * Image placeholder icon
 */
function ImagePlaceholderIcon() {
  return (
    <svg
      className="w-12 h-12 text-gray-300"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
      />
    </svg>
  );
}

/**
 * Text input area for text source type
 */
function TextInputArea({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-gray-700">Recipe Text</label>
      <p className="text-xs text-gray-400 mb-2">
        Paste the recipe text including ingredients and instructions.
      </p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste recipe text here...&#10;&#10;Example:&#10;Spaghetti Carbonara&#10;&#10;Ingredients:&#10;- 400g spaghetti&#10;- 4 eggs&#10;- 200g guanciale&#10;&#10;Instructions:&#10;1. Cook pasta in salted water&#10;2. Fry guanciale until crispy..."
        rows={12}
        className="px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm resize-y min-h-[300px] focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
      />
    </div>
  );
}

/**
 * URL input for URL source type
 */
function UrlInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-gray-700">Recipe URL</label>
      <p className="text-xs text-gray-400 mb-2">
        Enter the URL of a recipe page to extract.
      </p>
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://example.com/recipes/carbonara"
        className="px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
      />
    </div>
  );
}

/**
 * Image upload for image source type
 */
function ImageUpload({
  file,
  onChange,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    onChange(selectedFile);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const previewUrl = file ? URL.createObjectURL(file) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-5 items-start">
        {/* Preview area */}
        <div className="w-[200px] h-[150px] rounded-xl overflow-hidden flex-shrink-0 border-2 border-dashed border-gray-200">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Recipe image preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-50 flex items-center justify-center">
              <ImagePlaceholderIcon />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            className="hidden"
            aria-label="Upload recipe image"
          />

          <button
            type="button"
            onClick={handleUploadClick}
            className="inline-flex items-center gap-2 px-5 py-3 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50 hover:border-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
          >
            <UploadIcon />
            <span className="text-sm font-medium text-gray-700">
              {file ? 'Change Image' : 'Upload Image'}
            </span>
          </button>

          {file && (
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            >
              <XIcon />
              <span>Remove image</span>
            </button>
          )}

          <p className="text-xs text-gray-400">
            Upload a photo of a recipe
            <br />
            Formats: JPG, PNG, WebP
          </p>
        </div>
      </div>

      {file && (
        <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
          <span className="font-medium">Selected file:</span> {file.name}
        </div>
      )}
    </div>
  );
}

/**
 * PDF placeholder icon
 */
function PdfPlaceholderIcon() {
  return (
    <svg
      className="w-12 h-12 text-gray-300"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
      />
    </svg>
  );
}

/**
 * PDF upload for pdf source type
 */
function PdfUpload({
  file,
  onChange,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    onChange(selectedFile);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-5 items-start">
        {/* Preview area */}
        <div className="w-[200px] h-[150px] rounded-xl overflow-hidden flex-shrink-0 border-2 border-dashed border-gray-200">
          <div className="w-full h-full bg-gray-50 flex flex-col items-center justify-center">
            <PdfPlaceholderIcon />
            {file && (
              <span className="text-xs text-gray-500 mt-2 px-2 text-center truncate max-w-full">
                {file.name}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="hidden"
            aria-label="Upload recipe PDF"
          />

          <button
            type="button"
            onClick={handleUploadClick}
            className="inline-flex items-center gap-2 px-5 py-3 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50 hover:border-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
          >
            <UploadIcon />
            <span className="text-sm font-medium text-gray-700">
              {file ? 'Change PDF' : 'Upload PDF'}
            </span>
          </button>

          {file && (
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            >
              <XIcon />
              <span>Remove PDF</span>
            </button>
          )}

          <p className="text-xs text-gray-400">
            Upload a PDF recipe file
            <br />
            Format: PDF
          </p>
        </div>
      </div>

      {file && (
        <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
          <span className="font-medium">Selected file:</span> {file.name}
        </div>
      )}
    </div>
  );
}

export function SourceInputSection({
  sourceType,
  onSourceTypeChange,
  textContent,
  onTextContentChange,
  urlContent,
  onUrlContentChange,
  imageFile,
  onImageFileChange,
  pdfFile,
  onPdfFileChange,
  onExtract,
  isExtracting,
}: SourceInputSectionProps) {
  const canExtract =
    (sourceType === 'text' && textContent.trim().length > 0) ||
    (sourceType === 'url' && urlContent.trim().length > 0) ||
    (sourceType === 'image' && imageFile !== null) ||
    (sourceType === 'pdf' && pdfFile !== null);

  return (
    <section className="bg-white rounded-xl p-6 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
        Recipe Source
      </h2>

      <SourceTypeTabs value={sourceType} onChange={onSourceTypeChange} />

      {/* Content area based on source type */}
      <div className="mb-6">
        {sourceType === 'text' && (
          <TextInputArea value={textContent} onChange={onTextContentChange} />
        )}
        {sourceType === 'url' && <UrlInput value={urlContent} onChange={onUrlContentChange} />}
        {sourceType === 'image' && (
          <ImageUpload file={imageFile} onChange={onImageFileChange} />
        )}
        {sourceType === 'pdf' && (
          <PdfUpload file={pdfFile} onChange={onPdfFileChange} />
        )}
      </div>

      {/* Extract button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onExtract}
          disabled={!canExtract || isExtracting}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isExtracting ? (
            <>
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Extracting...
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                />
              </svg>
              Extract Recipe
            </>
          )}
        </button>
      </div>
    </section>
  );
}
